import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface EmailTemplateFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_url: string;
  file_category: "image" | "document" | "video" | "other";
  created_at: string;
}

const emailTemplateFilesQueryKey = "email-template-files";

// == Query Hooks ==

async function fetchEmailTemplateFiles(templateId: string): Promise<EmailTemplateFile[]> {
  const response = await fetch(`/api/email-templates/${templateId}/files`);
  if (!response.ok) {
    throw new Error("Failed to fetch files");
  }
  const result = await response.json();
  return result.data || [];
}

export function useEmailTemplateFiles(templateId: string, enabled = true) {
  return useQuery<EmailTemplateFile[]>({
    queryKey: [emailTemplateFilesQueryKey, templateId],
    queryFn: () => fetchEmailTemplateFiles(templateId),
    enabled: !!templateId && enabled,
  });
}

// == Mutation Hooks ==

interface UploadFileResponse {
  success: boolean;
  data?: EmailTemplateFile;
  error?: string;
}

async function uploadEmailTemplateFile(
  templateId: string,
  file: File
): Promise<UploadFileResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("templateId", templateId);

  const response = await fetch("/api/email-templates/files/upload", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to upload file");
  }

  return response.json();
}

export function useUploadEmailTemplateFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, file }: { templateId: string; file: File }) =>
      uploadEmailTemplateFile(templateId, file),
    onSuccess: (data, variables) => {
      // Invalidate the files list for this template
      void queryClient.invalidateQueries({
        queryKey: [emailTemplateFilesQueryKey, variables.templateId],
      });
      toast.success("File uploaded successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to upload file");
    },
  });
}

// == Standalone File Upload (for HTML email page without template) ==

async function uploadStandaloneFile(file: File): Promise<UploadFileResponse> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/api/email-templates/files/standalone-upload", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to upload file");
  }

  return response.json();
}

export function useUploadStandaloneFile() {
  return useMutation({
    mutationFn: (file: File) => uploadStandaloneFile(file),
    onSuccess: () => {
      toast.success("File uploaded successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to upload file");
    },
  });
}

// == Delete File Mutation ==

async function deleteEmailTemplateFile(fileId: string): Promise<void> {
  const response = await fetch(`/api/email-templates/files/${fileId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to delete file");
  }
}

export function useDeleteEmailTemplateFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) => deleteEmailTemplateFile(fileId),
    onSuccess: () => {
      // Invalidate all files queries
      void queryClient.invalidateQueries({
        queryKey: [emailTemplateFilesQueryKey],
      });
      toast.success("File deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete file");
    },
  });
}

// == Rename File Mutation ==

interface RenameFileResponse {
  success: boolean;
  data?: {
    id: string;
    fileName: string;
  };
  error?: string;
}

async function renameEmailTemplateFile(
  fileId: string,
  fileName: string
): Promise<RenameFileResponse> {
  const response = await fetch(`/api/email-templates/files/${fileId}/rename`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fileName }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to rename file");
  }

  return response.json();
}

export function useRenameEmailTemplateFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, fileName }: { fileId: string; fileName: string }) =>
      renameEmailTemplateFile(fileId, fileName),
    onSuccess: () => {
      // Invalidate all files queries
      void queryClient.invalidateQueries({
        queryKey: [emailTemplateFilesQueryKey],
      });
      toast.success("File renamed successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to rename file");
    },
  });
}
